export enum LaunchComponent {
  ROOM = 'Room',
  CONTENT_DEVELOPMENT_DASHBOARD = 'Content Development Dashboard',
  NETWORK = 'Network',
  LEGACY_NETWORK = 'LegacyNetwork',
  SOC_SIM = 'SOCSim',
  THREAT_HUNTER = 'ThreatHunter',
  EXAM = 'Exam',
  KOTH = 'KOTH',
  IMAGE_IMPORT = 'ImageImport',
  IMAGE_CREATION = 'ImageCreation',
}

export enum CostCenter {
  ROOM = 'Room',
  CONTENT_DEVELOPMENT_DASHBOARD = 'Content Development Dashboard',
  NETWORK = 'Network',
  LEGACY_NETWORK = 'LegacyNetwork',
  SOC_SIM = 'SOCSim',
  THREAT_HUNTER = 'ThreatHunter',
  EXAM = 'Exam',
  KOTH = 'KOTH',
  ROOM_OPEN_VPN = 'RoomOpenVPN',
  CONTENT = 'Content',
}

type BaseTagParams = {
  launchComponent: LaunchComponent;
  userId: string;
  userTier: string;
};

type RoomTagParams = BaseTagParams & {
  costCenter: CostCenter.ROOM;
  roomCode: string;
  upload: string;
  name: string;
};

//UserId omitted as cron typically triggers legacy network creation, not a user
type LegacyNetworkTagParams = Omit<BaseTagParams, 'userId' | 'userTier'> & {
  costCenter: CostCenter.LEGACY_NETWORK;
  networkTemplate: string;
  name: string;
};

type SOCSimTagParams = BaseTagParams & {
  costCenter: CostCenter.SOC_SIM;
  scenarioName: string;
  upload: string;
  name: string;
  scenario: string;
};

type NetworkTagParams = BaseTagParams & {
  costCenter: CostCenter.NETWORK;
  networkInstanceId: string;
  networkTemplate: string;
};

type ContentDevelopmentTagParams = BaseTagParams & {
  costCenter: CostCenter.CONTENT_DEVELOPMENT_DASHBOARD;
  upload: string;
};

type ThreatHunterTagParams = BaseTagParams & {
  costCenter: CostCenter.THREAT_HUNTER;
  name: string;
  upload: string;
  scenario: string;
  scenarioName: string;
};

type ExamTagParams = BaseTagParams & {
  costCenter: CostCenter.EXAM;
};

type KothTagParams = BaseTagParams & {
  costCenter: CostCenter.KOTH;
  machine: string;
  upload: string;
};

// Discriminated union for different cost center types
export type InstanceTagParams =
  | RoomTagParams
  | LegacyNetworkTagParams
  | SOCSimTagParams
  | NetworkTagParams
  | ContentDevelopmentTagParams
  | ThreatHunterTagParams
  | ExamTagParams
  | KothTagParams;

type Tag = {
  Key: string;
  Value: string;
};

/**
 * Creates a tag object, only including it if the value is defined
 */
const createTag = (key: string, value: string | undefined): Tag | null => {
  return value ? { Key: key, Value: value } : null;
};

export const constructInstanceTags = (params: InstanceTagParams): Tag[] => {
  // Extract common fields
  const { costCenter, launchComponent } = params;
  const userId = 'userId' in params ? params.userId : undefined;
  const userTier = 'userTier' in params ? params.userTier : undefined;
  const upload = 'upload' in params ? params.upload : undefined;
  const roomCode = 'roomCode' in params ? params.roomCode : undefined;
  const scenarioName = 'scenarioName' in params ? params.scenarioName : undefined;
  const scenario = 'scenario' in params ? params.scenario : undefined;
  const networkInstanceId = 'networkInstanceId' in params ? params.networkInstanceId : undefined;
  const networkTemplate = 'networkTemplate' in params ? params.networkTemplate : undefined;
  const machine = 'machine' in params ? params.machine : undefined;
  const name = 'name' in params ? params.name : undefined;
  // Create tags, filtering out null values
  const tags: (Tag | null)[] = [
    createTag('thm:room-code', roomCode),
    createTag('thm:user-id', userId),
    createTag('thm:user-tier', userTier),
    createTag('thm:upload', upload),
    createTag('thm:machine', machine),
    createTag('thm:scenario-name', scenarioName),
    createTag('thm:scenario', scenario),
    createTag('thm:launch-component', launchComponent),
    createTag('thm:network-instance-id', networkInstanceId),
    createTag('thm:network-template', networkTemplate),
    { Key: 'CostCenter', Value: costCenter },
    createTag('Name', name),
  ];

  // Filter out null tags and return
  return tags.filter((tag): tag is Tag => tag !== null);
};

export type ImageImportTags = BaseTagParams & {
  costCenter: CostCenter.CONTENT;
  upload: string;
  uploadId: string;
  name: string;
};

export type ImageCreationTags = BaseTagParams & {
  costCenter: CostCenter.CONTENT_DEVELOPMENT_DASHBOARD;
  attackBoxImage?: boolean;
  upload: string;
  uploadId: string;
  name: string;
};

export const constructImageTags = (params: ImageImportTags | ImageCreationTags): Tag[] => {
  const { costCenter, launchComponent } = params;
  const userId = 'userId' in params ? params.userId : undefined;
  const userTier = 'userTier' in params ? params.userTier : undefined;
  const upload = 'upload' in params ? params.upload : undefined;
  const attackBoxImage = 'attackBoxImage' in params && params.attackBoxImage ? 'true' : undefined;
  const name = 'name' in params ? params.name : undefined;
  const uploadId = 'uploadId' in params ? params.uploadId : undefined;
  const tags: (Tag | null)[] = [
    createTag('thm:user-id', userId),
    createTag('thm:user-tier', userTier),
    createTag('thm:upload', upload),
    createTag('thm:upload-id', uploadId),
    createTag('thm:launch-component', launchComponent),
    createTag('CostCenter', costCenter),
    createTag('AttackBoxImage', attackBoxImage),
    createTag('Name', name),
  ];
  return tags.filter((tag): tag is Tag => tag !== null);
};
